import {
  PRIVATE_REFERENCE_URL_PARAMS,
  scrubUrlParams,
  scrubUrlsInText,
} from "@/lib/analytics/url-param-scrubber";

/**
 * Remove private references (the OpenAI Ads `oppref` and the sign-in `ct_attr`
 * handoff) from a Sentry error event, transaction, or breadcrumb before it is
 * sent. They arrive through page and request URLs: `request.url`,
 * `request.query_string`, referrer headers, navigation and fetch breadcrumbs,
 * span descriptions and attributes, and messages.
 *
 * Every string is checked, so new Sentry fields are covered without a field
 * list. Return a plain-data copy so SDK objects with getters or read-only
 * properties are never mutated. Unreadable properties are omitted; objects
 * beyond the traversal limit are truncated rather than sent unredacted.
 */
export function scrubPrivateReferences<T>(value: T): T {
  return scrubValue(value, 0, new WeakMap()) as T;
}

const MAX_DEPTH = 16;
const PRIVATE_KEYS: readonly string[] = PRIVATE_REFERENCE_URL_PARAMS;
// Query-string text without a path, as in Sentry's `request.query_string`.
const BARE_QUERY_PATTERN = /^\??[^\s/?#=&]+=[^\s]*$/;

function isPrivateQueryPair(value: unknown): boolean {
  return (
    Array.isArray(value) &&
    value.length === 2 &&
    typeof value[0] === "string" &&
    PRIVATE_KEYS.includes(value[0])
  );
}

function scrubBareQuery(value: string): string {
  const hasQuestionMark = value.startsWith("?");
  const scrubbed = scrubUrlParams(`/?${hasQuestionMark ? value.slice(1) : value}`, PRIVATE_KEYS);
  const questionMarkIndex = scrubbed.indexOf("?");
  const query = questionMarkIndex === -1 ? "" : scrubbed.slice(questionMarkIndex + 1);
  return hasQuestionMark && query ? `?${query}` : query;
}

function scrubString(value: string): string {
  if (!PRIVATE_KEYS.some((key) => value.includes(key))) return value;

  const scrubbedText = scrubUrlsInText(value, PRIVATE_KEYS);
  if (scrubbedText !== value) return scrubbedText;
  return BARE_QUERY_PATTERN.test(value) ? scrubBareQuery(value) : value;
}

function scrubValue(value: unknown, depth: number, seen: WeakMap<object, unknown>): unknown {
  if (typeof value === "string") return scrubString(value);
  if (!value || typeof value !== "object") return value;
  if (seen.has(value)) return seen.get(value);
  if (depth > MAX_DEPTH) return "[MaxDepth]";

  if (value instanceof Date) {
    // Preserve JSON's timestamp representation without calling a custom toJSON.
    return Number.isNaN(Date.prototype.getTime.call(value))
      ? null
      : Date.prototype.toISOString.call(value);
  }

  if (Array.isArray(value)) {
    const scrubbed: unknown[] = [];
    seen.set(value, scrubbed);
    for (let index = 0; index < value.length; index++) {
      try {
        const item = value[index];
        if (!isPrivateQueryPair(item)) {
          scrubbed.push(scrubValue(item, depth + 1, seen));
        }
      } catch {
        // Array elements can also be getters. Never fall back to raw data.
        scrubbed.push(null);
      }
    }
    return scrubbed;
  }

  const record = value as Record<string, unknown>;
  const scrubbed: Record<string, unknown> = {};
  seen.set(value, scrubbed);
  // Console breadcrumb arguments can contain Errors before Sentry normalizes
  // them. Their diagnostic fields are usually non-enumerable.
  const keys =
    value instanceof Error
      ? new Set([...Object.keys(record), "name", "message", "stack"])
      : Object.keys(record);
  for (const key of keys) {
    if (PRIVATE_KEYS.includes(key)) continue;
    try {
      // Defining an own data property also avoids the legacy __proto__ setter.
      Object.defineProperty(scrubbed, key, {
        value: scrubValue(record[key], depth + 1, seen),
        enumerable: true,
        configurable: true,
        writable: true,
      });
    } catch {
      // Reading SDK accessors can throw; omit the field without exposing it.
    }
  }
  return scrubbed;
}
